import { AlertTriangle, Archive, Clock, FileCheck, FileText, Send } from 'lucide-react';

// ─── Status tints ───────────────────────────────────────────────

/**
 * The four status-chip fills, plus a resting neutral.
 *
 * Pointed at the shared tokens rather than at Tailwind's palette, because
 * Tailwind's ramps are cool-neutral and the brand palette is warm — a page
 * carrying both reads as two design systems arguing. Naming them here also
 * means these maps and `badgeVariants` cannot drift, and it is what makes the
 * whole set follow the theme into dark mode instead of staying on light fills.
 */
const tint = {
  info: 'bg-info-surface text-info',
  neutral: 'bg-muted text-muted-foreground',
  success: 'bg-success-surface text-success',
  warning: 'bg-warning-surface text-warning',
  error: 'bg-destructive-surface text-destructive',
} as const;

/** The same tints for chips that carry an outline. */
const outlined = {
  info: 'bg-info-surface text-info border-info-border',
  neutral: 'bg-muted text-muted-foreground border-border',
  success: 'bg-success-surface text-success border-success-border',
  warning: 'bg-warning-surface text-warning border-warning-border',
  error: 'bg-destructive-surface text-destructive border-destructive-border',
} as const;

// ─── Encounter Type ─────────────────────────────────────────────

export const ENCOUNTER_TYPES = ['initial', 'follow-up', 'urgent', 'telehealth', 'telephone'] as const;

export const encounterTypeLabels: Record<string, string> = {
  urgent: 'Urgent Visit',
  initial: 'Initial Visit',
  'follow-up': 'Follow-up',
  telehealth: 'Telehealth',
  telephone: 'Phone call',
};

export const encounterTypeColors: Record<string, string> = {
  urgent: tint.error,
  initial: tint.info,
  'follow-up': tint.success,
  telehealth: 'bg-category-plum-surface text-category-plum',
  telephone: tint.info,
};

export const encounterTypeBadgeStyles: Record<string, string> = {
  urgent: outlined.error,
  initial: outlined.info,
  'follow-up': outlined.success,
  telehealth: 'bg-category-plum-surface text-category-plum border-category-plum-border',
  telephone: outlined.info,
};

export const encounterTypeToggleColors: Record<string, string> = {
  initial: 'data-[state=on]:bg-info-surface data-[state=on]:text-info',
  'follow-up': 'data-[state=on]:bg-success-surface data-[state=on]:text-success',
  urgent: 'data-[state=on]:bg-destructive-surface data-[state=on]:text-destructive',
  telehealth: 'data-[state=on]:bg-category-plum-surface data-[state=on]:text-category-plum',
  telephone: 'data-[state=on]:bg-info-surface data-[state=on]:text-info',
};

export const encounterTypeTimelineDotColors: Record<string, string> = {
  initial: 'bg-primary',
  urgent: 'bg-destructive',
  'follow-up': 'bg-success',
  telehealth: 'bg-category-plum',
  telephone: 'bg-primary',
};

export const encounterTypeStatColors: Record<string, string> = {
  urgent: tint.error,
  initial: tint.info,
  'follow-up': tint.success,
  telehealth: 'bg-category-plum-surface text-category-plum',
  telephone: tint.info,
};

// ─── Encounter Status ───────────────────────────────────────────

export const encounterStatusColors: Record<string, string> = {
  draft: tint.warning,
  submitted: tint.info,
  complete: tint.success,
};

// ─── Document Status ────────────────────────────────────────────

export const documentStatusColors: Record<string, string> = {
  failed: tint.error,
  exported: tint.info,
  draft: tint.warning,
  generating: tint.info,
  archived: tint.neutral,
  signed: tint.success,
  finalized: tint.success,
};

export const documentStatusBadgeStyles: Record<string, string> = {
  failed: outlined.error,
  exported: outlined.info,
  draft: outlined.warning,
  generating: outlined.info,
  archived: outlined.neutral,
  signed: outlined.success,
  finalized: outlined.success,
  'in-progress': outlined.info,
};

export const documentStatusIcons: Record<string, typeof FileText> = {
  exported: Send,
  draft: FileText,
  archived: Archive,
  signed: FileCheck,
  finalized: FileCheck,
  failed: AlertTriangle,
};

// ─── Action Status ──────────────────────────────────────────────

export const actionStatusConfig: Record<string, { bg: string }> = {
  failed: { bg: tint.error },
  running: { bg: tint.info },
  pending: { bg: tint.warning },
  cancelled: { bg: tint.neutral },
  completed: { bg: tint.success },
};

// ─── Timeline Item Types ────────────────────────────────────────

export const timelineDotColors: Record<string, string> = {
  uploaded: 'bg-warning',
  lab: 'bg-category-plum',
  generated: 'bg-primary',
  encounter: 'bg-success',
  prescription: 'bg-category-rose',
};

export const timelineKindLabels: Record<string, string> = {
  lab: 'Lab',
  encounter: 'Encounter',
  prescription: 'Prescription',
  uploaded: 'Uploaded Document',
  generated: 'Generated Document',
};

// ─── Uploaded Document Type ─────────────────────────────────────

export const UPLOADED_DOCUMENT_TYPES = [
  { value: 'referral', label: 'Referral' },
  { value: 'lab_result', label: 'Lab Result' },
  { value: 'prescription', label: 'Prescription' },
  { value: 'medical_record', label: 'Medical Record' },
  { value: 'imaging', label: 'Imaging' },
  { value: 'other', label: 'Other' },
] as const;

export const uploadedDocumentTypeLabels: Record<string, string> = {
  other: 'Other',
  imaging: 'Imaging',
  referral: 'Referral',
  lab_result: 'Lab Result',
  prescription: 'Prescription',
  medical_record: 'Medical Record',
};

// ─── Extraction Status ──────────────────────────────────────────

export const extractionStatusColors: Record<string, string> = {
  failed: tint.error,
  pending: tint.neutral,
  extracting: tint.info,
  extracted: tint.success,
  not_applicable: tint.neutral,
};

// ─── Accepted File Types ────────────────────────────────────────

export const ACCEPTED_FILE_TYPES = {
  'image/png': ['.png'],
  'image/heic': ['.heic'],
  'image/heif': ['.heif'],
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
};

export const ACCEPTED_FILE_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.heic', '.heif', '.doc', '.docx'];

export const MAX_FILE_SIZE_MB = 25;
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
